import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import type {
  ExportBatch,
  ExportBatchWithCsv,
  ExportPreview,
  ExportType,
  QbEntities,
  QbEntity,
  QbStatus,
  QbSyncResult,
} from "../../shared/contracts";
import { addDays, isIsoDate, payPeriodFor } from "../../shared/time";
import type { BusinessSettings } from "../../shared/types";
import { Empty, Field } from "../components/ui";
import { get, post } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { useApi } from "../lib/useApi";
import {
  ConfirmDialog,
  downloadText,
  fmtIsoDate,
  isUnavailable,
  LoadState,
  PageHeader,
  RadioCards,
  ReasonDialog,
  thisWeek,
  toApiError,
  ukIsoDate,
  useConfig,
  useSettings,
} from "./common";
import { connectErrorText, groupSkipped } from "./qbText";

const TYPE_LABEL: Record<ExportBatch["type"], string> = {
  payroll_summary: "Payroll hours summary",
  timesheet_csv: "Timesheet detail",
  qb_sync: "Sent to QuickBooks",
};

type Preset = "this" | "last" | "custom";

/** Minimal CSV parser (quoted fields, doubled quotes, CRLF) for showing a preview table. */
export function parseCsv(text: string, maxRows = Infinity): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length && rows.length < maxRows; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += ch;
  }
  if ((field || row.length) && rows.length < maxRows) {
    row.push(field);
    rows.push(row);
  }
  // A leading BOM would otherwise show in the first header.
  if (rows[0]?.[0]) rows[0][0] = rows[0][0].replace(/^﻿/, "");
  return rows;
}

function usePeriod(settings: BusinessSettings | undefined) {
  const [preset, setPreset] = useState<Preset>("last");
  const today = thisWeek().today;
  const [custom, setCustom] = useState({ from: thisWeek().monday, to: today });
  const periods = useMemo(() => {
    if (!settings) return null;
    const current = payPeriodFor(today, settings.payPeriod, settings.payPeriodAnchor);
    const last = payPeriodFor(addDays(current.start, -1), settings.payPeriod, settings.payPeriodAnchor);
    return { current, last };
  }, [settings, today]);
  const range =
    preset === "custom"
      ? custom
      : periods
        ? preset === "this"
          ? { from: periods.current.start, to: periods.current.end }
          : { from: periods.last.start, to: periods.last.end }
        : null;
  return { preset, setPreset, custom, setCustom, periods, range };
}

function PreviewPanel({
  preview,
  onCreate,
  creating,
}: {
  preview: ExportPreview;
  onCreate: () => void;
  creating: boolean;
}) {
  const rows = useMemo(() => parseCsv(preview.csv, 11), [preview.csv]);
  const [header, ...body] = rows;
  const excluded = preview.excludedPending + preview.excludedOpen;
  return (
    <section className="card stack adm-section">
      <h2>
        Preview: {TYPE_LABEL[preview.type]}, {ukIsoDate(preview.periodStart)} to{" "}
        {ukIsoDate(preview.periodEnd)}
      </h2>
      <div className="adm-tiles">
        <div className="adm-tile">
          <div className="stat">{preview.rowCount}</div>
          <div className="label">{preview.type === "payroll_summary" ? "People" : "Rows"}</div>
        </div>
        <div className="adm-tile">
          <div className="stat">{preview.visitCount}</div>
          <div className="label">Visits included</div>
        </div>
        <div className={`adm-tile ${preview.flaggedCount ? "warn" : "zero"}`}>
          <div className="stat">{preview.flaggedCount}</div>
          <div className="label">Flagged visits</div>
        </div>
        <div className={`adm-tile ${preview.alreadyExported ? "info" : "zero"}`}>
          <div className="stat">{preview.alreadyExported}</div>
          <div className="label">Already exported</div>
        </div>
      </div>
      {excluded > 0 && (
        <div className="banner warn">
          {preview.excludedPending > 0 && (
            <div>
              {preview.excludedPending} {preview.excludedPending === 1 ? "visit is" : "visits are"} waiting
              for approval and won't be included.
            </div>
          )}
          {preview.excludedOpen > 0 && (
            <div>
              {preview.excludedOpen} {preview.excludedOpen === 1 ? "visit is" : "visits are"} still open or
              missing a clock-out and won't be included.
            </div>
          )}
          <div>
            Sort {excluded === 1 ? "it" : "them"} out on <Link to="/admin">Today</Link> first if they should
            be paid in this period.
          </div>
        </div>
      )}
      {preview.alreadyExported > 0 && (
        <div className="banner info">
          {preview.alreadyExported} {preview.alreadyExported === 1 ? "visit was" : "visits were"} already in
          an earlier batch and {preview.alreadyExported === 1 ? "is" : "are"} left out. Void that batch below
          to export {preview.alreadyExported === 1 ? "it" : "them"} again.
        </div>
      )}
      {preview.missingQbNames.length > 0 && (
        <div className="banner warn">
          No QuickBooks name set for: {preview.missingQbNames.join(", ")}. Their app name will be used, which
          must match QuickBooks exactly. <Link to="/admin/people">Set names on Cleaners</Link>.
        </div>
      )}
      {header && body.length > 0 ? (
        <>
          <div className="table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  {header.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((r, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: preview rows have no identity
                  <tr key={i}>
                    {r.map((c, j) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                      <td key={j} className="nowrap">
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.rowCount > body.length && (
            <p className="muted small">
              Showing the first {body.length} of {preview.rowCount} rows.
            </p>
          )}
        </>
      ) : (
        <Empty>Nothing to export for this period.</Empty>
      )}
      <div className="row">
        <button className="btn primary" onClick={onCreate} disabled={creating || preview.visitCount === 0}>
          {creating ? "Creating…" : "Create batch & download"}
        </button>
        <span className="muted small">
          The visits are marked as exported so they won't be exported twice.
        </span>
      </div>
    </section>
  );
}

function BatchHistory({ batches, onVoided }: { batches: ExportBatch[]; onVoided: () => void }) {
  const [voiding, setVoiding] = useState<ExportBatch | null>(null);
  if (!batches.length) return <Empty>No exports yet.</Empty>;
  return (
    <>
      <div className="table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th className="num">#</th>
              <th>Type</th>
              <th>Period</th>
              <th>Created</th>
              <th className="num">Visits</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} className={b.voided ? "muted" : ""}>
                <td className="num">{b.id}</td>
                <td>{TYPE_LABEL[b.type] ?? b.type}</td>
                <td className="nowrap">
                  {ukIsoDate(b.periodStart)} – {ukIsoDate(b.periodEnd)}
                </td>
                <td>
                  {fmtDateTime(b.createdAt)}
                  {b.createdByName && <div className="muted small">{b.createdByName}</div>}
                </td>
                <td className="num">{b.visitCount}</td>
                <td>
                  {b.voided ? (
                    <>
                      <span className="badge danger">Voided</span>
                      {b.voidReason && <div className="muted small">{b.voidReason}</div>}
                    </>
                  ) : (
                    <span className="badge ok">Exported</span>
                  )}
                </td>
                <td>
                  <span className="row" style={{ gap: 4, flexWrap: "nowrap" }}>
                    {b.filename && (
                      <a
                        className="btn sm"
                        href={`/api/admin/exports/${b.id}/download`}
                        download={b.filename}
                      >
                        Download
                      </a>
                    )}
                    {!b.voided && b.type !== "qb_sync" && (
                      <button className="btn ghost sm" onClick={() => setVoiding(b)}>
                        Void
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {voiding && (
        <ReasonDialog
          title={`Void batch #${voiding.id}?`}
          intro={
            <p>
              Its {voiding.visitCount} visits can then be exported again. Only do this if the file wasn't
              used, or you've undone it in QuickBooks.
            </p>
          }
          placeholder="e.g. Wrong period, re-exporting"
          confirmLabel="Void batch"
          danger
          onConfirm={async (reason) => {
            await post(`/admin/exports/${voiding.id}/void`, { reason });
            onVoided();
          }}
          onClose={() => setVoiding(null)}
        />
      )}
    </>
  );
}

function EntityTable({ items }: { items: QbEntity[] }) {
  if (!items.length) return <Empty>None found in QuickBooks.</Empty>;
  return (
    <div className="table-wrap" style={{ maxHeight: 320, overflow: "auto" }}>
      <table className="adm-table">
        <thead>
          <tr>
            <th>Name in QuickBooks</th>
            <th>ID</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id}>
              <td>{i.name}</td>
              <td className="adm-code">{i.id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ENTITY_TABS: { key: keyof QbEntities; label: string; hint: string }[] = [
  { key: "employees", label: "Employees", hint: "Put the ID on the cleaner's page (QuickBooks ID)." },
  { key: "vendors", label: "Suppliers", hint: "For self-employed cleaners: put the ID on their page." },
  { key: "customers", label: "Customers", hint: "Put the ID on the site's page (QuickBooks customer ID)." },
  {
    key: "items",
    label: "Services",
    hint: "For reference. Time sent to QuickBooks gets the service whose name matches the service name in Settings, if there is one (otherwise none). It is always sent as Not billable: the Billable setting only affects the CSV export.",
  },
];

function SkippedList({ skipped }: { skipped: QbSyncResult["skipped"] }) {
  const groups = groupSkipped(skipped);
  return (
    <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
      {groups.map((g) => (
        <li key={g.text}>
          {g.text}{" "}
          <span className="muted small">
            ({g.visitIds.length} {g.visitIds.length === 1 ? "visit" : "visits"}:{" "}
            {g.visitIds.slice(0, 20).map((id, i) => (
              <span key={id}>
                {i > 0 && ", "}
                <Link to={`/admin/visits/${id}`}>{i + 1}</Link>
              </span>
            ))}
            {g.visitIds.length > 20 && <> and {g.visitIds.length - 20} more</>})
          </span>
        </li>
      ))}
    </ul>
  );
}

function QuickBooksPanel({
  range,
  onSynced,
}: {
  range: { from: string; to: string } | null;
  /** Called after a send that created a batch, so Past exports can show it. */
  onSynced: () => void;
}) {
  const status = useApi<QbStatus>("/admin/quickbooks/status");
  const [entities, setEntities] = useState<QbEntities | null>(null);
  const [tab, setTab] = useState<keyof QbEntities>("employees");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<QbSyncResult | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [confirmSync, setConfirmSync] = useState(false);

  async function run<T>(label: string, fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(label);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(toApiError(e).message);
      return undefined;
    } finally {
      setBusy(null);
    }
  }

  const s = status.data;
  return (
    <section className="card stack adm-section">
      <h2>QuickBooks Online</h2>
      <LoadState state={status} unavailable="The QuickBooks connection isn't available on this server yet.">
        {() =>
          s?.connected ? (
            <>
              <p>
                <span className="badge ok">Connected</span> Company ID{" "}
                <span className="adm-code">{s.realmId}</span>
                {s.connectedAt && <> · since {fmtDateTime(s.connectedAt)}</>}
                {s.environment === "sandbox" && <span className="badge warn"> Sandbox</span>}
              </p>
              <div className="row">
                <button
                  className="btn primary"
                  onClick={() => setConfirmSync(true)}
                  disabled={!range || !!busy}
                >
                  {busy === "sync" ? "Sending…" : "Send period to QuickBooks"}
                </button>
                <button
                  className="btn"
                  onClick={() =>
                    void run("entities", async () =>
                      setEntities(await get<QbEntities>("/admin/quickbooks/entities")),
                    )
                  }
                  disabled={!!busy}
                >
                  {busy === "entities" ? "Loading…" : entities ? "Reload IDs" : "Show IDs for matching"}
                </button>
                <button className="btn ghost" onClick={() => setConfirmDisconnect(true)} disabled={!!busy}>
                  Disconnect
                </button>
              </div>
              <p className="muted small">
                Sending creates a time activity in QuickBooks for each finished visit in the chosen period
                that hasn't been sent before. People and sites need their QuickBooks IDs set first.
              </p>
            </>
          ) : (
            <>
              <p>
                Connect to send hours straight into QuickBooks as time activities, instead of importing a CSV.
              </p>
              <div className="row">
                <button
                  className="btn primary"
                  disabled={!!busy}
                  onClick={() =>
                    void run("connect", async () => {
                      const r = await post<{ url: string }>("/admin/quickbooks/connect");
                      window.location.assign(r.url);
                    })
                  }
                >
                  {busy === "connect" ? "Opening QuickBooks…" : "Connect to QuickBooks"}
                </button>
              </div>
            </>
          )
        }
      </LoadState>
      {error && <div className="banner danger">{error}</div>}
      {result && (
        <div className={`banner ${result.skipped.length ? "warn" : "ok"}`}>
          <div>
            Created {result.created} time {result.created === 1 ? "activity" : "activities"} in QuickBooks
            {result.batchId != null && <> (batch #{result.batchId})</>}.
          </div>
          {result.skipped.length > 0 && (
            <>
              <div>
                Not sent: {result.skipped.length} {result.skipped.length === 1 ? "visit" : "visits"}.
              </div>
              <SkippedList skipped={result.skipped} />
            </>
          )}
        </div>
      )}
      {entities && (
        <div className="stack">
          <h3>IDs for matching</h3>
          <div className="adm-tabs">
            {ENTITY_TABS.map((t) => (
              <button key={t.key} aria-pressed={tab === t.key} onClick={() => setTab(t.key)}>
                {t.label} ({entities[t.key].length})
              </button>
            ))}
          </div>
          <p className="muted small">{ENTITY_TABS.find((t) => t.key === tab)?.hint}</p>
          <EntityTable items={entities[tab]} />
        </div>
      )}
      {confirmDisconnect && (
        <ConfirmDialog
          title="Disconnect QuickBooks?"
          confirmLabel="Disconnect"
          danger
          onConfirm={async () => {
            status.setData(await post<QbStatus>("/admin/quickbooks/disconnect"));
            setEntities(null);
          }}
          onClose={() => setConfirmDisconnect(false)}
        >
          <p>Nothing already in QuickBooks is changed. You can connect again at any time.</p>
        </ConfirmDialog>
      )}
      {confirmSync && range && (
        <ConfirmDialog
          title="Send to QuickBooks?"
          confirmLabel="Send"
          onConfirm={async () => {
            setResult(null);
            const r = await post<QbSyncResult>("/admin/quickbooks/sync", range);
            setResult(r);
            if (r.batchId != null) onSynced();
            // The server may have dropped a connection QuickBooks no longer accepts.
            if (r.skipped.some((k) => k.reason === "reconnect_needed")) void status.reload();
          }}
          onClose={() => setConfirmSync(false)}
        >
          <p>
            Visits from {ukIsoDate(range.from)} to {ukIsoDate(range.to)} will be created as time activities in
            QuickBooks.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

export function ExportsPage() {
  const settings = useSettings();
  const config = useConfig();
  const batches = useApi<ExportBatch[]>("/admin/exports");
  const [params, setParams] = useSearchParams();
  const qb = params.get("qb");
  const qbReason = params.get("reason");
  const [type, setType] = useState<ExportType>("payroll_summary");
  const period = usePeriod(settings.data);
  const [preview, setPreview] = useState<ExportPreview | null>(null);
  const [busy, setBusy] = useState<"preview" | "create" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const range = period.range;
  // A cleared date input gives "", which must not reach the server as from= / to=.
  const datesChosen = !!range && isIsoDate(range.from) && isIsoDate(range.to);
  const rangeOk = datesChosen && range.from <= range.to;

  // A changed type or period makes the old preview stale.
  const key = `${type}|${range?.from}|${range?.to}`;
  useEffect(() => {
    void key;
    setPreview(null);
  }, [key]);

  async function loadPreview() {
    if (!range || !rangeOk) return;
    setBusy("preview");
    setError(null);
    setMessage(null);
    try {
      const q = new URLSearchParams({ type, from: range.from, to: range.to });
      setPreview(await get<ExportPreview>(`/admin/exports/preview?${q}`));
    } catch (e) {
      const err = toApiError(e);
      setError(isUnavailable(err) ? "Exports aren't available on this server yet." : err.message);
    } finally {
      setBusy(null);
    }
  }

  async function create() {
    if (!range || !rangeOk) return;
    setBusy("create");
    setError(null);
    try {
      const b = await post<ExportBatchWithCsv>("/admin/exports", { type, from: range.from, to: range.to });
      downloadText(b.filename ?? `export-${b.id}.csv`, b.csv);
      setMessage(`Batch #${b.id} created and downloaded (${b.visitCount} visits).`);
      setPreview(null);
      void batches.reload();
    } catch (e) {
      setError(toApiError(e).message);
    } finally {
      setBusy(null);
    }
  }

  const fmtRange = (r: { start: string; end: string }) =>
    `${fmtIsoDate(r.start, { weekday: undefined })} – ${fmtIsoDate(r.end, { weekday: undefined })}`;

  return (
    <div className="page">
      <PageHeader title="Exports" sub="CSV files for QuickBooks and payroll. Times are UK time." />
      {qb === "connected" && (
        <div className="banner ok">
          <div className="row between">
            <span>QuickBooks is connected.</span>
            <button className="btn ghost sm" onClick={() => setParams({}, { replace: true })}>
              ✕
            </button>
          </div>
        </div>
      )}
      {qb === "error" && (
        <div className="banner danger">
          <div className="row between">
            <span>{connectErrorText(qbReason)}</span>
            <button className="btn ghost sm" onClick={() => setParams({}, { replace: true })}>
              ✕
            </button>
          </div>
        </div>
      )}

      <section className="card stack">
        <RadioCards
          legend="What to export"
          name="type"
          value={type}
          onChange={setType}
          columns
          options={[
            {
              value: "payroll_summary",
              title: TYPE_LABEL.payroll_summary,
              description: "One row per person with their total hours, to type into the pay run.",
            },
            {
              value: "timesheet_csv",
              title: TYPE_LABEL.timesheet_csv,
              description:
                "One row per visit, laid out for importing time activities into QuickBooks Online.",
            },
          ]}
        />
        <fieldset className="adm-fieldset">
          <legend>Period</legend>
          {settings.error && <div className="banner danger">{settings.error.message}</div>}
          <div className="grid cols-3">
            <Field label="Choose">
              <select value={period.preset} onChange={(e) => period.setPreset(e.target.value as Preset)}>
                <option value="last">
                  Last pay period{period.periods ? `: ${fmtRange(period.periods.last)}` : ""}
                </option>
                <option value="this">
                  This pay period{period.periods ? `: ${fmtRange(period.periods.current)}` : ""}
                </option>
                <option value="custom">Custom dates</option>
              </select>
            </Field>
            {period.preset === "custom" && (
              <>
                <Field label="From">
                  <input
                    type="date"
                    value={period.custom.from}
                    onChange={(e) => period.setCustom({ ...period.custom, from: e.target.value })}
                  />
                </Field>
                <Field label="To">
                  <input
                    type="date"
                    value={period.custom.to}
                    onChange={(e) => period.setCustom({ ...period.custom, to: e.target.value })}
                  />
                </Field>
              </>
            )}
          </div>
          {range && !datesChosen && <div className="banner warn">Choose both dates.</div>}
          {datesChosen && !rangeOk && (
            <div className="banner warn">The “From” date is after the “To” date.</div>
          )}
        </fieldset>
        <div className="row">
          <button className="btn" onClick={loadPreview} disabled={!rangeOk || !!busy}>
            {busy === "preview" ? "Checking…" : "Preview"}
          </button>
        </div>
        {error && <div className="banner danger">{error}</div>}
        {message && <div className="banner ok">{message}</div>}
      </section>

      {preview && <PreviewPanel preview={preview} onCreate={create} creating={busy === "create"} />}

      <section className="adm-section">
        <h2>Past exports</h2>
        <LoadState state={batches} unavailable="Exports aren't available on this server yet.">
          {(list) => <BatchHistory batches={list} onVoided={() => void batches.reload()} />}
        </LoadState>
      </section>

      {config.data?.quickbooksEnabled && (
        <QuickBooksPanel range={rangeOk ? range : null} onSynced={() => void batches.reload()} />
      )}
    </div>
  );
}
